/**
 * DefaultCell Component
 *
 * Fallback cell component for fields without specific cell renderers.
 * Uses simple formatting for consistent display.
 *
 * Arrays are displayed using the ArrayCell component with ChipsList pattern
 * for consistent "+N" display with hover tooltips.
 */

import React from 'react';

import { ArrayCell } from './ArrayCell';

export interface DefaultCellProps {
  value: string | unknown[] | null | undefined;
  /** Maximum number of array items to show before "+N" counter (default: 1) */
  maxVisible?: number;
}

/**
 * Renders a default cell value with consistent styling
 *
 * Handles:
 * - Empty/null values → displays "-"
 * - Arrays → displays using ArrayCell with "+N" pattern and tooltip
 * - Strings → displays as-is
 * - Numbers/booleans → converts to string
 */
export const DefaultCell: React.FC<DefaultCellProps> = React.memo(
  ({ value, maxVisible = 1 }) => {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === '-'
    ) {
      return <span className="text-chariot-text-secondary">-</span>;
    }

    if (Array.isArray(value)) {
      return <ArrayCell values={value} maxVisible={maxVisible} />;
    }

    return <span className="text-chariot-text-primary">{String(value)}</span>;
  }
);

DefaultCell.displayName = 'DefaultCell';
